import {
  Injectable
} from "@angular/core";
import {
  AuthenticatedModel
} from "@app/models/authentication/authenticated.model";
import {
  JwtHelperService
} from "@auth0/angular-jwt";
import {
  Router
} from "@angular/router";
import {
  HttpClient
} from "@angular/common/http";
import {
  User
} from "@app/models/administration/user.model";
import {
  TokenService
} from "./token.service";
import {
  UserUpdatePasswordViewModel
} from "@app/models/administration/userUpdatePasswordViewModel";
import {
  RoleService
} from "../role/role.service";
import {
  FunctionSummaryModel
} from "@app/models/functioninfo/functionSummary.model";
import {
  PermissionService
} from "../permission/permission.service";
import {
  DnaModuleService
} from "../dna/dna.module.service";
import {
  FunctionService
} from "../functioninfo/function.service";
import {
  Observable
} from "rxjs";

@Injectable({ providedIn: "root" })
export class UserService {
  private authenticatedUser: AuthenticatedModel;
  public userBlockVisible: boolean;
  private service = 'UserService';

  private userFunctions: FunctionSummaryModel[] = [];

  private generalRoutes: string[] = ['/', '/login', '/500', '/dashboard'];

  constructor(private http: HttpClient,
    private router: Router,
    private tokenService: TokenService,
    private roleService: RoleService,
    private permissionService: PermissionService,
    private dnaModuleService: DnaModuleService,
    private functionService: FunctionService) {
    // initially visible
    this.userBlockVisible = true;
  }

  public getVisibility() {
    return this.userBlockVisible;
  }

  public setVisibility(stat = true) {
    this.userBlockVisible = stat;
  }

  public toggleVisibility() {
    this.userBlockVisible = !this.userBlockVisible;
  }

  // tslint:disable-next-line: member-ordering
  public setUser = new Observable<AuthenticatedModel>((observer) => {
    const helper = new JwtHelperService();
    const user = new AuthenticatedModel();

    // get the embbeded information from token and save logged in user info
    // we check if the page refreshed and component cleaned. If yes we repopulate the user
    const rawToken = this.tokenService.get('token');

    if (!rawToken) {
      this.router.navigate(['/login']);
      return;
    }

    Object.assign(user, JSON.parse(helper.decodeToken(rawToken).sub));

    this.authenticatedUser = user;

    observer.next(user);
    observer.complete();
  });

  // tslint:disable-next-line: member-ordering
  public get = new Observable<AuthenticatedModel>((observer) => {
    const options = { responseType: 'text' as 'text' };

    if (!this.authenticatedUser) {
      this.setUser.subscribe((result) => {
        this.authenticatedUser = result;

        this.http.get(`api/${this.service}/loadProfilePicture/${this.authenticatedUser.id}`, options).
          subscribe(
            (profilePictureResponse) => {
              this.authenticatedUser.profilePictureBase64 = profilePictureResponse;
              observer.next(this.authenticatedUser);
            },
            (error): void => {
              observer.next(this.authenticatedUser);
            });
      });
    }

    this.http.get(`api/${this.service}/loadProfilePicture/${this.authenticatedUser.id}`, options).
      subscribe(
        (profilePictureResponse) => {
          this.authenticatedUser.profilePictureBase64 = profilePictureResponse;
          observer.next(this.authenticatedUser);
        },
        (error): void => {
          observer.next(this.authenticatedUser);
        });
  });

  public getUserObs = new Observable<AuthenticatedModel>((observer) => {
    if (!this.authenticatedUser) {
      this.setUser.subscribe();
    }
    observer.next(this.authenticatedUser);
    observer.complete();
  });

  public getUserFunctions() {
    return new Observable<FunctionSummaryModel[]>((observer) => {
      if (this.userFunctions && this.userFunctions.length > 0) {
        observer.next(this.userFunctions);
        observer.complete();
      } else {
        if (this.authenticatedUser) {
          this.permissionService.getUserFunctions(this.authenticatedUser.id).subscribe(functions => {
            this.userFunctions = functions;
            observer.next(this.userFunctions);
            observer.complete();
          });
        } else {
          this.setUser.subscribe(x => {
            this.permissionService.getUserFunctions(this.authenticatedUser.id).subscribe(functions => {
              this.userFunctions = functions;
              observer.next(this.userFunctions);
              observer.complete();
            });
          });
        }
      }
    });
  }

  public getUserFunctionByUrl(url: string) {
    return new Observable<FunctionSummaryModel>((observer) => {
      this.getUserFunctions().subscribe(functions => {
        if (this.userFunctions && this.userFunctions.length > 0 && url) {
          const functionInfo = this.userFunctions.find(x => url.toLowerCase().indexOf(x.routePath.toLowerCase()) > -1);
          observer.next(functionInfo);
          observer.complete();
        } else {
          observer.next(null);
          observer.complete();
        }
      });
    });
  }

  public hasPermissionForUrl(url: string) {

    return new Observable<boolean>((observer) => {
      if (this.generalRoutes.findIndex(x => x === url) >= 0) {
        observer.next(true);
        observer.complete();
      }
      if (!this.authenticatedUser) {
        observer.next(false);
        observer.complete();
      }

      this.getUserFunctionByUrl(url).subscribe(functionModal => {
        if (functionModal && this.authenticatedUser.assignedFunctions.findIndex(x => x === functionModal.id) >= 0) {
          observer.next(true);
          observer.complete();
        } else {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  public getLoggedInUser() {
    return this.authenticatedUser;
  }

  public isGodUser() {
    return this.authenticatedUser.roles.findIndex(x => x === this.roleService.godUserRole.id) >= 0;
  }

  public isDnaAdminUser() {
    return this.authenticatedUser.roles.findIndex(x => x === this.roleService.dnaAdminUserRole.id) >= 0;
  }

  public isFreeDesignAssistantClient() {
    return this.authenticatedUser.roles.findIndex(x => x === this.roleService.freeDesignAssistantUserRole.id) >= 0;
  }

  public isPortalUser() {
    return this.authenticatedUser.roles.findIndex(x => x === this.roleService.portalUserRole.id) >= 0;
  }

  public canEditDNA(dnaClientId: number) {
    return this.dnaModuleService.canUserEditDna(dnaClientId);
  }

  public getUsersWithSameClient() {
    return this.http.get<Array<User>>(`api/${this.service}/GetUsersWithSameClient`);
  }

  public uploadImage(image) {
    return this.http.post(`api/${this.service}/upload-image/`, image, { reportProgress: true, observe: 'events' });
  }

  public updatePassword(userUpdatePasswordViewModel: UserUpdatePasswordViewModel) {
    return this.http.post(`api/${this.service}/update-password/`, userUpdatePasswordViewModel);
  }

  public getProfilePictureName() {
    return this.authenticatedUser.profilePictureName;
  }

  public logoutUser() {
    this.authenticatedUser = null;
    this.userFunctions = [];
  }

  public isUserADAuthenticated() {
    return this.http.get<boolean>(`api/${this.service}/IsUserADAuthenticated`);
  }

  public hasCCTVDesignerAccess() {
    if (this.isGodUser()) return true;
    return this.authenticatedUser.assignedFunctions.findIndex(x => x === this.functionService.cctvDesignerFunction.id) >= 0;
  }
}
